import angular from 'angular';
import angularBootstrap from 'angular-ui-bootstrap';
import statusMessage from '../status-message';
import i18n from '../i18n';
import wizardService from './wizard-service';
import wizardContentDirective from './wizard-content-directive';
import spinnerButton from '../spinner-button';
import messageBox from '../message-box';

/**
 * @ngdoc module
 * @name akamai.components.wizard
 *
 * @guideline https://design.akamai.com/components/wizard/
 *
 * @description The wizard control is a type of modal window that presents a user with a
 * sequence of dialog boxes that lead the user through a series of well-defined steps.
 * Tasks that are complex, infrequently performed, or unfamiliar may be easier to perform using
 * a wizard.
 *
 * @example index.js
 * function MyController(wizard, translate) {
 *   wizard.open({
 *     scope: wizardScope,
 *     controller: WizardController,
 *     title: 'Wizard Example',
 *     steps: [
 *       {
 *		   // name will be translated if key is provided
 *		   // nameValues also supported
 *         name: 'examples.wizard.step1',
 *         templateId: 'step1',
 *         validate: function(scope) {
 *             return true;
 *         },
 *         initialize: function(scope) {
 *            return true;
 *         },
 *         dispose: function(scope) {
 *            return true;
 *         }
 *       },
 *       {
 *         name: 'examples.wizard.step2',
 *         templateId: 'step2'
 *       }
 *     ]
 *   });
 * }
 *
 * @example setOnSubmitExample.js
 *
 * function wizardController($scope, $q, $timeout) {
 *   var times = 0;
 *
 *   $scope.setOnSubmit(function(){
 *     var deferral = $q.defer();
 *
 *     $timeout(function() {
 *       if (times % 2 === 0) {
 *         deferral.reject();
 *       } else {
 *         deferral.resolve();
 *       }
 *       times++;
 *       }, 3000);
 *
 *       return deferral.promise;
 *     });
 * }
 */
export default angular.module('akamai.components.wizard', [
  angularBootstrap,
  statusMessage.name,
  i18n.name,
  spinnerButton.name,
  messageBox.name
])

/**
 * @ngdoc service
 * @name akamai.components.wizard.wizard
 *
 * @description
 * A service that opens a wizard
 */

/**
 * @ngdoc method
 *
 * @name akamai.components.wizard.wizard#open
 *
 * @description Opens a wizard in a modal window
 *
 * @param {Object} options A hash with the options specified below.
 *
 * @param {constructor|string} [options.controller] A controller for the wizard that can
 * initialize scope.
 *
 * @param {string} [options.controllerAs] an alternative to the controller-as syntax,
 * matching the API of directive definitions. Requires the controller option to be
 * provided as well
 *
 * @param {Object} [options.resolve] Members that will be resolved and passed to the controller
 * as locals.
 *
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for
 * the wizard content body
 *
 * @param {boolean} [options.animation=true] Set to false to disable animations on new
 * modal/backdrop. Does not toggle animations for modals/backdrops that are already displayed.
 *
 * @param {boolean} [options.keyboard=true] Indicates whether the dialog should be closable
 * by hitting the ESC key.
 *
 * @param {string} [options.backdropClass] Additional CSS class(es) to be added to a modal
 * backdrop template.
 *
 * @param {string} [options.openedClass=modal-open] class added to the `body` element
 * when the modal is opened. Defaults to `modal-open`
 *
 * @param {string} size Optional suffix of modal window class. The modal class for wizard is
 * `wizard`. The value used is appended to the `wizard-` class, i.e. a value of `sm`
 * gives `wizard-sm`
 *
 * @param {function(): Promise} [options.scope.setOnSubmit] This function will be called
 * when the user clicks the submit button. If the promise is resolved, the wizard will
 * close and the `successMessage` will be displayed. If the promise is rejected, the wizard
 * will remain open and the `errorMessage` will be displayed. If a promise is not
 * returned by `setOnSubmit` the wizard will close.
 *
 * @param {function()} [options.scope.close] Calling this function will close the wizard
 *
 * @param {function()} [options.scope.resetSteps(stepNumber] Calling this function
 * will set to 'not visited' all steps after given `stepNumber`.
 * `stepNumber` is a zero-based index (i.e. 0 is step 1, 1 is step 2, etc).
 *
 * @param {string|TranslateKey} [options.title] A title for the wizard
 *
 * @param {string|TranslateKey} [options.submitLabel=Submit] A label for the submit button
 *
 * @param {string|TranslateKey} [options.cancelLabel=Cancel] A label for the cancel button
 *
 * @param {string|TranslateKey} [options.previousLabel=Previous] A label for the previous
 * button
 *
 * @param {string|TranslateKey} [options.nextLabel=Next] A label for the next button
 *
 * @param {string|TranslateKey} [options.successMessage=The action has been completed.] The
 * message shown in a [status-message](status-message/) when the modal-window submit process
 * completes successfully
 *
 * @param {string|TranslateKey} [options.errorMessage=The action can't be completed.] The error
 * message shown at the top of the modal-window when an error occurs during the submit process.
 *
 * @param {boolean} [options.doNotShowMessage=false] If set to true, will not display a status
 * @param {Object[]} options.steps An array of step objects with the
 * following properties. Note: the requirements for templating are one of
 * `template`, `templateId` or `templateUrl`
 *
 * - `name` {string} The name of the wizard step. Shown in the wizard step
 *   navigation bar
 *
 * - `template` {string} An inline template to render within the body of
 *   the wizard.
 *
 * - `templateId` {string} A templateId to retrieve the template used to
 *    render within the body of the wizard
 *
 * - `templateUrl` {string} A URL referencing a template to render within
 *   the body of the wizard
 *
 * - `validate` {function(Scope): boolean} A validation function that returns true when
 *   the step is valid. The step's next button is enabled when the function
 *   returns true.
 *
 * - `initialize` {function(Scope): Promise} This function will be called before a step is
 *   activated. If the promise is resolved, the step will be activated. If the promise is
 *   rejected, an error message with the rejected reason will be displayed.
 *
 * - `dispose` {function(Scope): Promise} When a step is activated, this function will be called
 *   for the previous step. If the promise is resolved, the step will be activated.
 *   If the promise is rejected, an error message with the rejected reason will be displayed.
 *
 * @return {Object} An instance of the wizard with the following
 * properties:
 *
 * - `close` (function) A method to close the wizard that accepts a result
 *    as an argument.
 *
 * - `dismiss` (function) A method to dismiss the wizard, rejecting the
 *   `result` promise.
 *
 * - `result` (Promise) A promise representing the result when the wizard
 *    is closed.
 *
 */
  .factory('wizard', wizardService)

/**
 * @private
 * @name akamai.components.wizard.directive:akamWizardContent
 * @description
 * A simple directive that injects step templates into the body of the wizard
 */
  .directive('akamWizardContent', wizardContentDirective);
